// LandingHome.jsx (updated version)
import React, { useEffect, useRef } from "react";
import Layout from "../layout/Layout";
import OrganizationSlider from "../components/organ/OrganizationSlider"; 
import {
  getPosts,
  getPostsByVisit,
  getSliderNews,
  getTopSlider,
} from "../services/postService";
import { getContactConfigs } from "../services/configService";
import { imgBaseUrl } from "../utils/api";


const LandingHome = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    const mount = async () => {
      try {
        const res = await fetch("/landing/index.html");
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        // Extract only the inner of .main-container to avoid duplicating <html><head>.
        const main = doc.querySelector(".main-container") || doc.body;
        // Remove header/footer from injected content; global layout will render them
        const tmp = main.cloneNode(true);
        tmp.querySelector("header")?.remove();
        tmp.querySelector("footer")?.remove();

        // Rewrite asset URLs to point under /landing/ (apply to tmp)
        const prefix = (value) =>
          typeof value === "string" && value.startsWith("uploads/")
            ? `/landing/${value}`
            : value;

        tmp.querySelectorAll("[src]").forEach((el) => {
          el.setAttribute("src", prefix(el.getAttribute("src")));
        });
        tmp.querySelectorAll("[data-src]").forEach((el) => {
          el.setAttribute("data-src", prefix(el.getAttribute("data-src")));
        });
        tmp.querySelectorAll("[data-bg]").forEach((el) => {
          el.setAttribute("data-bg", prefix(el.getAttribute("data-bg")));
        });
        tmp.querySelectorAll("source[srcset]").forEach((el) => {
          const val = el.getAttribute("srcset");
          if (val && val.trim().startsWith("uploads/"))
            el.setAttribute("srcset", `/landing/${val.trim()}`);
        });

        if (containerRef.current) {
          containerRef.current.innerHTML = tmp.innerHTML;
        }

        const attachTabHandlers = (root, itemSel, paneSel) => {
          if (!root) return;
          root.addEventListener("click", (e) => {
            const item = e.target.closest(itemSel);
            if (!item || !root.contains(item)) return;
            e.preventDefault();
            // Activate clicked tab
            Array.from(root.querySelectorAll(itemSel)).forEach((it) =>
              it.classList.remove("active")
            );
            item.classList.add("active");
            // Show corresponding pane
            const anchor = item.querySelector("a");
            const targetSelector = anchor ? anchor.getAttribute("href") : null;
            const panes = Array.from(root.querySelectorAll(paneSel));
            panes.forEach((p) => p.classList.remove("show"));
            if (targetSelector) {
              const targetPane = root.querySelector(targetSelector);
              if (targetPane) targetPane.classList.add("show");
            } else {
              const items = Array.from(root.querySelectorAll(itemSel));
              const idx = items.indexOf(item);
              if (idx > -1 && panes[idx]) panes[idx].classList.add("show");
            }
          });
        };

        // Lazy image fallback (assign data-src to src, and data-bg to background-image)
        const lazyImgs =
          containerRef.current?.querySelectorAll("img[data-src]") || [];
        lazyImgs.forEach((img) => {
          const ds = img.getAttribute("data-src");
          if (ds) img.setAttribute("src", ds);
        });
        const lazyBgs =
          containerRef.current?.querySelectorAll("[data-bg]") || [];
        lazyBgs.forEach((node) => {
          const db = node.getAttribute("data-bg");
          if (db) node.style.backgroundImage = `url(${db})`;
        });

        // Rewrite tab titles and limit to required tabs
        const rewriteTabs = (root, itemSel, paneSel, labels, idPrefix) => {
          const items = Array.from(root.querySelectorAll(itemSel));
          const panes = Array.from(root.querySelectorAll(paneSel));
          // Ensure there are at least as many panes; remove excess beyond labels
          for (let i = 0; i < labels.length; i++) {
            const it = items[i];
            const pn = panes[i];
            const paneId = `${idPrefix}-${i + 1}`;
            if (it) {
              const a = it.querySelector("a") || it;
              if (a) {
                a.textContent = labels[i];
                a.setAttribute("href", `#${paneId}`);
              }
            }
            if (pn) pn.id = paneId;
          }
          // Remove extra items and panes beyond labels length
          for (let j = labels.length; j < items.length; j++) {
            items[j].parentNode && items[j].parentNode.removeChild(items[j]);
          }
          for (let j = labels.length; j < panes.length; j++) {
            panes[j].parentNode && panes[j].parentNode.removeChild(panes[j]);
          }
          // Default: show first pane
          const first = root.querySelector(`#${idPrefix}-1`);
          if (first) {
            panes.forEach((p, idx) => {
              if (p) p.classList.toggle("show", idx === 0);
            });
            const firstItem = root.querySelector(itemSel);
            if (firstItem) firstItem.classList.add("active");
          }
        };

        const hostForNews = containerRef.current?.querySelector(".news");
        if (hostForNews) {
          rewriteTabs(
            hostForNews,
            ".news__tab__item",
            ".news__tabs__pane",
            ["اخبار", "پروژه‌ها"],
            "custom-news-pane"
          );
          attachTabHandlers(
            hostForNews,
            ".news__tab__item",
            ".news__tabs__pane"
          );
        }
        const hostForProjects =
          containerRef.current?.querySelector(".projects");
        if (hostForProjects) {
          rewriteTabs(
            hostForProjects,
            ".projects__tab__item",
            ".projects__tabs__pane",
            ["مناقصه", "مزایده"],
            "custom-proj-pane"
          );
          attachTabHandlers(
            hostForProjects,
            ".projects__tab__item",
            ".projects__tabs__pane"
          );
        }

        // Define functions expected by inline handlers
        window.blurOthers = function (currentBox) {
          const boxes = document.querySelectorAll(".ministry__box");
          boxes.forEach((box) => {
            if (box !== currentBox) box.classList.add("blurred");
          });
        };
        window.clearBlur = function () {
          const boxes = document.querySelectorAll(".ministry__box");
          boxes.forEach((box) => box.classList.remove("blurred"));
        };

        // Execute inline scripts inside main (so sliders/tabs initialize)
        const scripts = tmp.querySelectorAll("script");
        scripts.forEach((s) => {
          const n = document.createElement("script");
          if (s.src) {
            const src = s.getAttribute("src");
            n.src = src?.startsWith("uploads/") ? `/landing/${src}` : src;
          } else {
            n.text = s.textContent || "";
          }
          document.body.appendChild(n);
        });

        // Build fixed 4 quick links under hero (replace systems slider contents)
        try {
          const sysSlider = containerRef.current?.querySelector(
            ".systems .system__slider"
          );
          //== slider 2
          if (sysSlider) {
            sysSlider.setAttribute("data-fixed", "1");
            sysSlider.innerHTML = "";
            const links = [
              {
                href: "/news/1015",
                img: "/landing/uploads/mimt/assets/images/s7_1.svg",
                title: "مناقصه",
              },
              {
                href: "/news/1014",
                img: "/landing/uploads/mimt/assets/images/s1_1.svg",
                title: "مزایده",
              },
            ];
            links.forEach((l) => {
              const cell = document.createElement("div");
              cell.className = "system__cell";
              cell.innerHTML = `
                <div class="system__item">
                  <div class="system__hover__box"></div>
                  <img src="${l.img}" alt="${l.title}" class="system__img">
                  <h3 class="system__title">${l.title}</h3>
                  <a href="${l.href}" class="cover-link"></a>
                </div>
              `;
              sysSlider.appendChild(cell);
            });
          }
        } catch {}

        // Ensure interactions (sliders, tabs) initialize even if inline scripts raced.
        const ensureInteractions = () => {
          const $ = window.jQuery || window.$;
          const hasFlickity = $ && $.fn && typeof $.fn.flickity === "function";

          $(document).on("mouseenter", ".z-10", function () {
            $(this).removeClass("opacity-0").addClass("opacity-100");
          });

          // Hide overlay on mouse leave
          $(document).on("mouseleave", ".z-10", function () {
            $(this).removeClass("opacity-100").addClass("opacity-0");
          });
          // Fallback CSS adjustments already applied via project styles.
          // If jQuery + Flickity available, initialize sliders similar to template.
          if (hasFlickity) {
            
            setTimeout(() => {
  try {
              // Hero main slider
              //== big banner slider
              $(containerRef.current)
                .find(".main__slider")
                .each(function () {
                  const $el = $(this);
                  if ($el.data("flickity")) return;
                  $el.flickity({
                    wrapAround: true,
                    cellAlign: "right",
                    contain: true,
                    pageDots: true,
                    prevNextButtons: true,
                    rightToLeft: true,
                    imagesLoaded: true,
                    lazyLoad: true,
                    groupCells: 1,
                    draggable: true,
                    autoPlay: 6000,
                    selectedAttraction: 0.015,
                    friction: 0.4,
                  });
                });
              //== small slider
              $(containerRef.current)
                .find(".system__slider")
                .each(function () {
                  const $el = $(this);
                  if ($el.is("[data-fixed]")) return; // skip fixed quick links
                  if ($el.data("flickity")) return;
                  $el.flickity({
                    wrapAround: true,
                    cellAlign: "right",
                    contain: true,
                    pageDots: false,
                    prevNextButtons: true,
                    rightToLeft: true,
                    imagesLoaded: true,
                    lazyLoad: true,
                    groupCells: 1,
                    draggable: true,
                    autoPlay: 5000,
                    arrowShape: "",
                    selectedAttraction: 0.015,
                    friction: 0.4,
                  });
                });

              // News slider (right side) – show single image like template
              $(containerRef.current)
                .find(".news__slider")
                .each(function () {
                  const $el = $(this);
                  if ($el.data("flickity")) return;
                  $el.flickity({
                    wrapAround: true,
                    cellAlign: "right",
                    contain: true,
                    pageDots: false,
                    prevNextButtons: false,
                    rightToLeft: true,
                    imagesLoaded: true,
                    lazyLoad: true,
                    groupCells: 1,
                    arrowShape: "",
                    autoPlay: 6000,
                    selectedAttraction: 0.015,
                    friction: 0.4,
                  });
                });

              // News tabs click behavior (left side tabs)
              const tabItemClass1 = $(containerRef.current).find(
                ".news__tab__item"
              );
              const tabPaneClass2 = $(containerRef.current).find(
                ".news__tabs__pane"
              );
              tabItemClass1
                .off("click.landing")
                .on("click.landing", function (event) {
                  event.preventDefault();
                  const $this = $(this);
                  $this.addClass("active").siblings().removeClass("active");
                  tabPaneClass2.removeClass("show");
                  const target = $this.find("a").attr("href");
                  if (target)
                    $(containerRef.current).find(target).addClass("show");
                });

              // Projects tabs (if present)
              const pTabItem = $(containerRef.current).find(
                ".projects__tab__item"
              );
              const pTabPane = $(containerRef.current).find(
                ".projects__tabs__pane"
              );
              pTabItem
                .off("click.landing")
                .on("click.landing", function (event) {
                  event.preventDefault();
                  const $this = $(this);
                  $this.addClass("active").siblings().removeClass("active");
                  pTabPane.removeClass("show");
                  const target = $this.find("a").attr("href");
                  if (target) {
                    const $pane = $(containerRef.current)
                      .find(target)
                      .addClass("show");
                    // initialize slider inside pane when shown
                    $pane.find(".projects__slider").each(function () {
                      const $sl = $(this);
                      if ($sl.data("flickity")) return;
                      $sl.flickity({
                        wrapAround: false,
                        cellAlign: "right",
                        contain: true,
                        pageDots: true,
                        prevNextButtons: false,
                        rightToLeft: true,
                        imagesLoaded: true,
                        lazyLoad: true,
                        groupCells: 1,
                        draggable: true,
                        autoPlay: 5000,
                        arrowShape: "",
                        selectedAttraction: 0.015,
                        friction: 0.4,
                      });
                    });
                  }
                });
            } catch (e) {
              // swallow, will retry
            }

            }, 500);

            return true;
          }
          return false;
        };

        // Wait for jQuery + Flickity then initialize; retry a few times.
        let tries = 0;
        const timer = setInterval(() => {
          if (ensureInteractions() || tries++ > 10) {
            clearInterval(timer);
          }
        }, 300);

        // --------------------------
        // Populate dynamic content from APIs
        // --------------------------
        const bimg = (name) => {
          if (!name) return "";

          const src = String(name);

          if (/^https?:/i.test(src)) return src;

          const base = (imgBaseUrl || "").replace(/\/$/, "");
          const normalized = src
            .replace("http://10.20.30.12:92/", "")
            .replace(/^\//, "");
            
          return base ? `${base}/${normalized}` : `/${normalized}`;
        };


        const renderNewsItems = (posts = []) => {
          return posts
            .map(
              (p) => `
            <div class="morenews__item">
              <div class="morenews__img">
                ${
                  p.smallImageName || p.imageName
                    ? `<img src="${bimg(
                        p.smallImageName || p.imageName
                      )}" alt="${p.title}" class="fit-img">`
                    : ""
                }
                <a href="/post/${p.pageId}" class="cover-link"></a>
              </div>
              <div class="morenews__content">
                <h4 class="morenews__title">
                <a href="/post/${p.pageId}" class="morenews__link">${
                p.title
              }</a>
                <p class="morenews__lead" style="font-size: 1em; height:29px">${
                  p.shortDescription
                }</p>
                <p class="morenews__date" style="font-size: 0.9em;"><i class="niafam niafam-calendar" style="font-size: 0.75em;"></i>${
                  p.shamsiCreateDate
                }</p>
                </h4>
              </div>
            </div>
          `
            )
            .join("");
        };

        const renderNewsSlides = (posts = []) => {
          return posts
            .map(
              (p) => `
    <div class="news__cell">
      <div class="news__item">
        ${
          p.smallImageName || p.imageName
            ? `<img src="${bimg(p.imageName || p.smallImageName)}" alt="${
                p.title
              }" class="fit-img">`
            : ""
        }
        <div class="news__content">
          <p class="news__kicker"></p>
          <h4 class="news__title">${p.title}</h4>
        </div>
        <a href="/post/${p.pageId}" class="cover-link"></a>
      </div>
    </div>
  `
            )
            .join("");
        };

        const fillNewsTabs = async () => {
          try {
            //در تب اخبار 4 تا خبر پر بازدیدترین از گروه اخبار
            //getPostsByVisit
            const newsRes = await getPosts({
              pageId: 0,
              take: 4,
              selectedGroup: [1017],
            });
            //در تب پروژه ها 4 تا خبر پر بازدید گروه پروره ها
            const projRes = await getPosts({
              pageId: 0,
              take: 4,
              selectedGroup: [1009],
            });

            const host = containerRef.current;
            if (!host) return;
            // Find panes (first = اخبار, second = پروژه‌ها)
            const panes = host.querySelectorAll(".news__tabs__pane");
            if (panes[0])
              panes[0].innerHTML = `<div class="morenews">${renderNewsItems(
                newsRes.posts || []
              )}</div> <div class="mimt__arc__wrapper"><a href="/news/1017" class="mimt__arc">آرشیو <i class="niafam niafam-left2"></i></a></div>`;
            if (panes[1])
              panes[1].innerHTML = `<div class="morenews">${renderNewsItems(
                projRes.posts || []
              )}</div> <div class="mimt__arc__wrapper"><a href="/news/1009" class="mimt__arc">آرشیو <i class="niafam niafam-left2"></i></a></div>`;

            // Also fill the right-side news slider with the same content as "اخبار"
            const slider = host.querySelector(".news__slider");
            if (slider) {
              slider.innerHTML = renderNewsSlides(newsRes.posts || []);
              // If Flickity is already initialized, rebuild it to apply new slides
              try {
                const $ = window.jQuery || window.$;
                if ($ && $.fn && typeof $.fn.flickity === "function") {
                  const $el = $(slider);
                  if ($el.data("flickity")) {
                    $el.flickity("destroy");
                  }
                  $el.flickity({
                    wrapAround: false,
                    cellAlign: "right",
                    contain: true,
                    pageDots: true,
                    prevNextButtons: false,
                    rightToLeft: true,
                    imagesLoaded: true,
                    lazyLoad: true,
                    groupCells: 1,
                    draggable: true,
                    autoPlay: 6000,
                    arrowShape: "",
                    selectedAttraction: 0.015,
                    friction: 0.4,
                  });
                }
              } catch (_) {}
            }
          } catch (e) {
            console.error("fillNewsTabs error", e);
          }
        };
        //== چند رسانه ای
        const fillGallery = async () => {
          try {
            
            const galleryList = await getPosts({
              pageId: 0,
              take: 6,
              selectedGroup: [2000],
            });

            const host = containerRef.current;
            if (!host) return;

            const slider = host.querySelector(".news__gallery");
            if (slider) {
              slider.innerHTML = `
                <div class="-m-1 flex flex-row sm:flex-wrap md:-m-2">
                  <div class="flex w-full sm:w-1/2 flex-wrap lg:flex-row flex-row-reverse">

                    ${[0, 1, 2]
                      .map(
                        (i) => `
                    <div class="${i === 2 ? "w-full" : "w-1/2"} p-1 md:p-2">
                      <a href="/post/${
                        galleryList.posts[i].pageId
                      }" class="relative group block h-100">
                        <img
                          alt="gallery"
                          class="block h-100 w-full rounded-lg 2xl:rounded-2xl object-cover object-center transition duration-300 group-hover:brightness-50"
                          src="${bimg(galleryList.posts[i].imageName)}"
                        />
                        <div class="absolute inset-0 z-10 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <span class="text-white text-lg md:text-xl font-semibold text-center drop-shadow-lg">
                            ${galleryList.posts[i].title}
                          </span>
                        </div>
                      </a>
                    </div>
                    `
                      )
                      .join("")}

                  </div>
${galleryList.posts.length >3 ? `
                  <div class="flex w-full sm:w-1/2 flex-wrap">
                    ${[3, 4, 5]
                      .map(
                        (i) => `
                    <div class="${i === 3 ? "w-full" : "w-1/2"} p-1 md:p-2">
                      <a href="/post/${
                        galleryList.posts[i].pageId
                      }" class="relative group block ${i === 3 ? "" : "h-100"}">
                        <img
                          alt="gallery"
                          class="block h-100 w-full rounded-lg 2xl:rounded-2xl object-cover object-center transition duration-300 group-hover:brightness-50"
                          src="${bimg(galleryList.posts[i].imageName)}"
                        />
                        <div class="absolute inset-0 z-10 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <span class="text-white text-lg md:text-xl font-semibold text-center drop-shadow-lg">
                            ${galleryList.posts[i].title}
                          </span>
                        </div>
                      </a>
                    </div>
                    `
                      )
                      .join("")}
                  </div>`:''}
                </div>
              `;
            }

            
          } catch (e) {
            console.error("fillNewsTabs error", e);
          }
        };

        const fillThreeBoxes = async () => {
          try {
            const res = await getSliderNews();
            //const itemsoo = (res.posts || []).slice(0, 3);
            const mypost = await getPosts({ pageId: 0, take: 3, selectedGroup: [1030] });
            const items = mypost.posts

            const host = containerRef.current;
            if (!host) return;

            const boxes = await host.querySelectorAll(".ministry__box");
            boxes.forEach((box, idx) => {
              if (idx >= items.length) return;
              const p = items[idx];
              const text = (
                p.shortDescription ||
                p.description ||
                p.content ||
                ""
              ).trim();
              const snippet =
                text.length > 100 ? text.substring(0, 100) + "..." : text;
              const href = `/post/${p.pageId}`;

              // ✅ تصویر
              const img = box.querySelector("img");
              if (img && (p.imageName || p.smallImageName)) {
                img.src = bimg(p.imageName || p.smallImageName);
                img.alt = p.title;
              } else if (p.imageName || p.smallImageName) {
                box.style.backgroundImage = `url(${bimg(
                  p.imageName || p.smallImageName
                )})`;
              }

              // ✅ عنوان
              const titleEl = box.querySelector(".ministry__title");
              if (titleEl) titleEl.textContent = p.title;

              // ✅ متن خلاصه (جایگزین متن قبلی)
              const leadEl = box.querySelector(
                ".ministry__lead a.ministry__link2"
              );
              if (leadEl) {
                leadEl.href = href;
                leadEl.textContent = snippet;
              }

              // ✅ لینک "مشاهده بیشتر"
              const moreEl = box.querySelector(
                ".ministry__morebox a.ministry__more"
              );
              if (moreEl) {
                moreEl.href = href;
              }

              // ✅ لینک روی تصویر
              const imgLink = box.querySelector(".ministry__img a.cover-link");
              if (imgLink) imgLink.href = href;
            });
          } catch (e) {
            console.error("fillThreeBoxes error", e);
          }
        };

        const renderProjectSlides = (posts = []) => {
          const cells = posts
            .map(
              (p) => `
    <div class="projects__cell">
      <div class="projects__item">
        <div class="projects__content">
          <h3 class="projects__title">${p.title}</h3>
          <p class="projects__lead"><a href="/post/${
            p.pageId
          }" class="projects__link2">مشاهده جزئیات</a></p>
        </div>
        <div class="projects__img__wrapper">
          <div class="projects__img">
            ${
              p.smallImageName || p.imageName
                ? `<img src="${bimg(p.smallImageName || p.imageName)}" alt="${
                    p.title
                  }" class="fit-img"  style="object-fit:fill">`
                : ""
            }
            <a href="/post/${p.pageId}" class="cover-link"></a>
          </div>
        </div>
      </div>
    </div>
  `
            )
            .join("");
          return `<div class="projects__slider">${cells}</div>`;
        };

        const fillRunningProjects = async () => {
          try {
            //تب مناقصه مزایده
            // هر دو تب بر اساس selectedGroup:1007 طبق درخواست
            const [tenderRes, auctionRes] = await Promise.all([
              getPosts({ pageId: 0, take: 2, selectedGroup: [1015] }),
              getPosts({ pageId: 0, take: 2, selectedGroup: [1014] }),
            ]);

            const host = containerRef.current;
            if (!host) return;
            const panes = host.querySelectorAll(".projects__tabs__pane");
            if (panes[0]) {
              panes[0].innerHTML = renderProjectSlides(tenderRes.posts || []);
              panes[0].classList.add("show"); // ensure first pane visible
            }
            if (panes[1]) {
              panes[1].innerHTML = renderProjectSlides(auctionRes.posts || []);
            }
          } catch (e) {
            console.error("fillRunningProjects error", e);
          }
        };

        const fillTopSlider = async () => {
          try {
            const topSliderData = await getTopSlider();

            const host = containerRef.current;
            if (!host) return;

            //====================================================== small slider
            const system_slider = host.querySelector(".system__slider");
            system_slider.innerHTML = "";
            var smallSlider = topSliderData
              .filter((n) => n.type == 1)
              .map(
                (item) => `
    <div class="system__cell">
                <div class="system__item">
                  <div class="system__hover__box"></div>
                  <img src="${bimg(item.imageUrl)}" alt="${item.title}" class="system__img">
                  <h3 class="system__title">${item.title}</h3>
                  <a href="${item.link}" class="cover-link"></a>
                </div>
              </div>`
              )
              .join("");
            system_slider.innerHTML = smallSlider;


            //========================================================== big banner slider
            const topslider = host.querySelector(".main__slider");
            topslider.innerHTML = "";
            var tempdate = topSliderData
              .filter((n) => n.type == 0)
              .map(
                (item) => `
        <div class="main__slider__cell">
              <div class="main__slider__item">
                  <picture>
                      <source srcset="${item.imageUrl}" media="(max-width: 575px)">
                      <img src="${bimg(item.imageUrl)}" alt="" class="fit-img lazy">
                  </picture>
                  <div class="notif d-none">
                      <div class="quarter-circle"></div>
                      <h3 class="notif__title"></h3>
                      <p class="notif__lead"></p>
                  </div>
              </div>
          </div>
  `
              )
              .join("");

            topslider.innerHTML = tempdate;
            if (topslider.current) {
              topslider.current.destroy();
            }

            
          } catch (e) {
            console.error("topslider error", e);
          }
        };

        const fillFooterFromConfig = async () => {
          try {
            const payload = await getContactConfigs();
            const configs =
              payload?.items &&
              Array.isArray(payload.items) &&
              payload.items.length
                ? payload.items[0]
                : payload;
            const address = configs?.address || configs?.["OfO_OfO3"] || "";
            const phone =
              configs?.phone || configs?.tel || configs?.["OfU,U?U+"] || "";
            const mobile =
              configs?.mobile || configs?.['U.U^O"O\u0015UOU,'] || "";
            const email = configs?.email || configs?.["O\u0015UOU.UOU,"] || "";
            const eitaa = configs?.eitaa || "";
            const instagram =
              configs?.instagram ||
              configs?.["O\u0015UOU+O3OfO\u0015U_OfO\u0015U."] ||
              "";
            const telegram =
              configs?.telegram || configs?.["OfU,U_OfO\u0015U."] || "";
            const whatsapp =
              configs?.whatsapp || configs?.["U^O\u0015OfO3O\u0015U_"] || "";

            const footer = containerRef.current?.querySelector("footer.footer");
            if (!footer) return;

            // Address
            const addrP = footer
              .querySelector(".contact .contact__item i.niafam-map1")
              ?.parentElement?.querySelector("p");
            if (addrP && address) addrP.textContent = address;

            // Phone/Mobile
            const phoneP = footer
              .querySelector(".contact .contact__item i.niafam-call")
              ?.parentElement?.querySelector("p");
            if (phoneP && (phone || mobile)) {
              phoneP.textContent = `${phone ? `تلفن: ${phone}` : ""}${
                phone && mobile ? " | " : ""
              }${mobile ? `موبایل: ${mobile}` : ""}`;
            }

            // Email
            const emailP = footer
              .querySelector(
                ".contact .contact__item i.niafam-email-2, .contact .contact__item i.niafam-mail"
              )
              ?.parentElement?.querySelector("p");
            if (emailP && email) {
              emailP.innerHTML = `ایمیل: <a href="mailto:${email}">${email}</a>`;
            }

            // Socials
            const ul = footer.querySelector("ul.socialmedia");
            if (ul) {
              ul.innerHTML = "";
              const makeLi = (href, html) => {
                const li = document.createElement("li");
                li.innerHTML = `<a href="${href}" target="_blank" rel="noopener noreferrer">${html}</a>`;
                return li;
              };
              if (instagram)
                ul.appendChild(
                  makeLi(
                    `https://instagram.com/${instagram}`,
                    '<i class="fa-brands fa-instagram"></i>'
                  )
                );
              if (telegram)
                ul.appendChild(
                  makeLi(
                    `https://t.me/${telegram}`,
                    '<i class="fa-brands fa-telegram"></i>'
                  )
                );
              if (eitaa)
                ul.appendChild(
                  makeLi(eitaa,
                    '<span class="fa-eitaa w-20px h-20px" />'
                  )
                );
              if (whatsapp)
                ul.appendChild(
                  makeLi(
                    `https://wa.me/${whatsapp}`,
                    '<i class="fa-brands fa-whatsapp"></i>'
                  )
                );
              if (email && false)
                ul.appendChild(
                  makeLi(
                    `mailto:${email}`,
                    '<i class="fa-solid fa-envelope"></i>'
                  )
                );
            }
          } catch (e) {
            console.error("fillFooterFromConfig error", e);
          }
        };

        fillTopSlider();
        //fillGallery(); //== چند رسانه ای
        fillNewsTabs(); //== تب خبر و پروژه
        fillThreeBoxes();
        fillRunningProjects();
        fillFooterFromConfig();
      } catch (e) {
        console.error("Failed to load landing html", e);
      }
    };
    mount();
  }, []);

  return (
    <Layout>
      <div ref={containerRef} />
      <OrganizationSlider />
    </Layout>
  );
};

export default LandingHome;